import {
  TransportItemType,
  type EventEvent,
  type TraceEvent,
  type TransportItem,
} from '@grafana/faro-web-sdk';

/**
 * Telemetry privacy guard (AGENTS.md / security rule: "No PII exposure in
 * browser console or URLs").
 *
 * The admin portal's routes and API paths embed sensitive identifiers
 * (`/applications/:id`, `/transactions/:id`, `/withdrawals/:id`, `/audit`, …).
 * Faro attaches the current page URL to every signal, navigation events carry
 * from/to URLs, and the tracing instrumentation records each request's URL on
 * its HTTP span. Left unfiltered, account/transaction/application ids would
 * leave the browser.
 *
 * `sanitizeTelemetry` is registered as Faro's `beforeSend` hook and strips
 * id-like segments from every URL across all three vectors before the item is
 * queued for the collector. It is exhaustively guarded — a hook that throws
 * silently drops telemetry, so scrubbing failures must never escape.
 */

/**
 * A path segment that looks like an identifier rather than a route name:
 * a UUID, a 4+ digit number (account / transaction id), or a 16+ char opaque
 * token. Route words ("dashboard", "fee-settings") stay below the token
 * threshold and pass through untouched.
 */
const ID_SEGMENT =
  /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|\d{4,}|[A-Za-z0-9_-]{16,})$/i;

/** OTLP span attribute keys that hold a request URL across semconv versions. */
const TRACE_URL_KEYS = new Set(['http.url', 'http.target', 'url.full', 'url.path']);

/** Minimal structural view of the OTLP trace payload we need to walk. */
interface OtlpAttribute {
  key?: string;
  value?: { stringValue?: string };
}
interface OtlpSpan {
  attributes?: OtlpAttribute[];
}
interface OtlpScopeSpans {
  spans?: OtlpSpan[];
}
interface OtlpResourceSpans {
  scopeSpans?: OtlpScopeSpans[];
}

/**
 * Replace id-like path segments with `:id` and drop query-string values.
 * Handles both absolute (`https://host/transactions/123?x=y`) and relative
 * (`/transactions/123`) URLs; returns the input unchanged if it cannot be parsed.
 */
export function redactUrl(raw: string | undefined): string | undefined {
  if (!raw) {
    return raw;
  }
  try {
    const wasAbsolute = /^[a-z][a-z0-9+.-]*:\/\//i.test(raw);
    const parsed = new URL(raw, 'http://redacted.invalid');
    const path = parsed.pathname
      .split('/')
      .map((segment) => (ID_SEGMENT.test(segment) ? ':id' : segment))
      .join('/');
    // Query strings can carry tokens / PII — keep only the fact one existed.
    const search = parsed.search ? '?<redacted>' : '';
    return wasAbsolute ? `${parsed.origin}${path}${search}` : `${path}${search}`;
  } catch {
    return raw;
  }
}

export function sanitizeTelemetry(item: TransportItem): TransportItem {
  try {
    // 1. Page URL meta — attached to every exception, log, event and span.
    if (item.meta.page?.url) {
      item.meta.page.url = redactUrl(item.meta.page.url);
    }

    // 2. Navigation / route-change events — `fromUrl`, `toUrl`, `url` attrs.
    if (item.type === TransportItemType.EVENT) {
      const attributes = (item.payload as EventEvent).attributes;
      if (attributes) {
        for (const key of Object.keys(attributes)) {
          if (/url$/i.test(key)) {
            attributes[key] = redactUrl(attributes[key]) ?? attributes[key];
          }
        }
      }
    }

    // 3. HTTP client spans — the request URL recorded by TracingInstrumentation.
    if (item.type === TransportItemType.TRACE) {
      const resourceSpans = ((item.payload as TraceEvent).resourceSpans ??
        []) as unknown as OtlpResourceSpans[];
      for (const resource of resourceSpans) {
        for (const scope of resource.scopeSpans ?? []) {
          for (const span of scope.spans ?? []) {
            for (const attribute of span.attributes ?? []) {
              if (
                attribute.key &&
                TRACE_URL_KEYS.has(attribute.key) &&
                attribute.value?.stringValue
              ) {
                attribute.value.stringValue =
                  redactUrl(attribute.value.stringValue) ?? attribute.value.stringValue;
              }
            }
          }
        }
      }
    }
  } catch {
    // Never let sanitization break telemetry delivery.
  }
  return item;
}
